import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';

export type ActivityType = 'event' | 'run' | 'badge' | 'route' | 'user_joined' | 'achievement_earned';
export type ActivityFilterType = 'all' | 'following' | 'mine' | 'local';

export interface ActivityItem {
  id: string;
  type: ActivityType;
  timestamp: string;
  title: string;
  description: string;
  metadata?: {
    distance?: number;
    duration?: number;
    eventType?: string;
    status?: string;
    startTime?: string;
    photos?: string[];
    trailCount?: number;
    distanceFromHome?: number;
  };
  userId: string;
  user?: {
    id: string;
    username: string;
    avatar_url?: string;
    featured_vehicle_photo_url?: string;
  };
  vehicle?: {
    nickname?: string;
    year?: number;
    make?: string;
    model?: string;
  };
}

interface LocalFilterParams {
  latitude: number;
  longitude: number;
  radiusMiles: number;
}

export const useActivityFeed = (
  userId?: string, 
  filter: ActivityFilterType = 'mine',
  localParams?: LocalFilterParams
) => {
  return useQuery({
    queryKey: ['activity-feed', userId, filter, localParams],
    queryFn: async () => {
      if (!userId && filter === 'mine') {
        return [];
      }
      
      if (filter === 'local' && !localParams) {
        return [];
      }

      const activities: ActivityItem[] = [];

      // Get list of followed users if needed
      let followedUserIds: string[] = [];
      if (filter === 'following' && userId) {
        const { data: follows } = await supabase
          .from('user_follows')
          .select('following_id')
          .eq('follower_id', userId);
        
        followedUserIds = follows?.map(f => f.following_id) || [];
        
        if (followedUserIds.length === 0) {
          return []; // No one to follow, return early
        }
      }

      // Determine which users to query for
      const targetUserIds = filter === 'mine' 
        ? [userId!] 
        : filter === 'following' 
        ? followedUserIds 
        : []; // 'all' filter uses empty array - we handle it separately

      // Fetch runs with user and vehicle data
      // For 'local' filter, we use a spatial query
      if (filter === 'local' && localParams) {
        // Convert miles to meters for PostGIS (1 mile = 1609.34 meters)
        const radiusMeters = localParams.radiusMiles * 1609.34;
        
        // Use RPC call to get runs within radius
        // Cast to any since the function won't be in the auto-generated types until refresh
        const { data: localRuns, error: localError } = await (supabase.rpc as any)(
          'get_runs_within_radius',
          {
            center_lat: localParams.latitude,
            center_lon: localParams.longitude,
            radius_meters: radiusMeters,
            limit_count: 20
          }
        ) as { data: Array<{ id: string; distance_miles: number }> | null; error: any };

        if (localError) {
          console.error('Error fetching local runs:', localError);
        } else if (localRuns && localRuns.length > 0) {
          const runIds = localRuns.map(r => r.id);
          
          // Fetch full run data with user and vehicle
          const { data: fullRuns } = await supabase
            .from('runs')
            .select(`
              *,
              user:user_id (
                id,
                username,
                avatar_url
              ),
              vehicle:vehicle_id (
                nickname,
                year,
                make,
                model
              )
            `)
            .in('id', runIds)
            .order('created_at', { ascending: false });

          // Create distance map from RPC results
          const distanceMap = new Map<string, number>(localRuns.map(r => [r.id, r.distance_miles]));

          // Fetch segment counts
          const { data: segmentCounts } = await supabase
            .from('run_segments')
            .select('run_id, trail_segment_id')
            .in('run_id', runIds);

          const trailCountMap = new Map<string, number>();
          if (segmentCounts) {
            segmentCounts.forEach(seg => {
              const count = trailCountMap.get(seg.run_id) || 0;
              trailCountMap.set(seg.run_id, count + 1);
            });
          }

          // Fetch featured vehicles for users
          const userIds = [...new Set(fullRuns?.map(r => r.user_id) || [])];
          const { data: featuredVehicles } = await supabase
            .from('user_vehicles')
            .select('user_id, primary_photo_url')
            .in('user_id', userIds)
            .eq('is_featured', true);
          const featuredVehicleMap = new Map(featuredVehicles?.map(v => [v.user_id, v.primary_photo_url]) || []);

          fullRuns?.forEach(run => {
            const photos = Array.isArray(run.photo_urls) 
              ? (run.photo_urls as string[]).filter(url => typeof url === 'string')
              : [];
            
            activities.push({
              id: run.id,
              type: 'run',
              timestamp: run.created_at,
              title: run.title || run.ai_generated_title || `${run.equipment_type.replace('_', ' ')} Run`,
              description: `Completed a run`,
              metadata: {
                distance: run.distance_miles,
                duration: run.duration_minutes,
                startTime: run.start_time,
                photos: photos,
                trailCount: trailCountMap.get(run.id) || 0,
                distanceFromHome: distanceMap.get(run.id) ?? undefined,
              },
              userId: run.user_id,
              user: run.user ? {
                id: run.user.id,
                username: run.user.username || 'Anonymous',
                avatar_url: run.user.avatar_url,
                featured_vehicle_photo_url: featuredVehicleMap.get(run.user_id) || undefined,
              } : undefined,
              vehicle: run.vehicle ? {
                nickname: run.vehicle.nickname,
                year: run.vehicle.year,
                make: run.vehicle.make,
                model: run.vehicle.model,
              } : undefined,
            });
          });
        }
      } else if (filter === 'all') {
        // "All" filter - fetch all public runs from all users
        const { data: runs, error: runsError } = await supabase
          .from('runs')
          .select(`
            *,
            user:user_id (
              id,
              username,
              avatar_url
            ),
            vehicle:vehicle_id (
              nickname,
              year,
              make,
              model
            )
          `)
          .eq('visibility', 'public')
          .order('created_at', { ascending: false })
          .limit(20);

        if (runsError) {
          console.error('Error fetching runs:', runsError);
        } else if (runs) {
          const runIds = runs.map(r => r.id);
          const { data: segmentCounts } = await supabase
            .from('run_segments')
            .select('run_id, trail_segment_id')
            .in('run_id', runIds);

          const trailCountMap = new Map<string, number>();
          if (segmentCounts) {
            segmentCounts.forEach(seg => {
              const count = trailCountMap.get(seg.run_id) || 0;
              trailCountMap.set(seg.run_id, count + 1);
            });
          }

          // Fetch featured vehicles for users
          const userIds = [...new Set(runs.map(r => r.user_id))];
          const { data: featuredVehicles } = await supabase
            .from('user_vehicles')
            .select('user_id, primary_photo_url')
            .in('user_id', userIds)
            .eq('is_featured', true);
          const featuredVehicleMap = new Map(featuredVehicles?.map(v => [v.user_id, v.primary_photo_url]) || []);

          runs.forEach(run => {
            const photos = Array.isArray(run.photo_urls) 
              ? (run.photo_urls as string[]).filter(url => typeof url === 'string')
              : [];
            
            activities.push({
              id: run.id,
              type: 'run',
              timestamp: run.created_at,
              title: run.title || run.ai_generated_title || `${run.equipment_type.replace('_', ' ')} Run`,
              description: `Completed a run`,
              metadata: {
                distance: run.distance_miles,
                duration: run.duration_minutes,
                startTime: run.start_time,
                photos: photos,
                trailCount: trailCountMap.get(run.id) || 0,
              },
              userId: run.user_id,
              user: run.user ? {
                id: run.user.id,
                username: run.user.username || 'Anonymous',
                avatar_url: run.user.avatar_url,
                featured_vehicle_photo_url: featuredVehicleMap.get(run.user_id) || undefined,
              } : undefined,
              vehicle: run.vehicle ? {
                nickname: run.vehicle.nickname,
                year: run.vehicle.year,
                make: run.vehicle.make,
                model: run.vehicle.model,
              } : undefined,
            });
          });
        }
      } else if (targetUserIds.length > 0) {
        const { data: runs, error: runsError } = await supabase
          .from('runs')
          .select(`
            *,
            user:user_id (
              id,
              username,
              avatar_url
            ),
            vehicle:vehicle_id (
              nickname,
              year,
              make,
              model
            )
          `)
          .in('user_id', targetUserIds)
          .order('created_at', { ascending: false })
          .limit(20);

        if (runsError) {
          console.error('Error fetching runs:', runsError);
        } else if (runs) {
          // Count trail segments for each run
          const runIds = runs.map(r => r.id);
          const { data: segmentCounts } = await supabase
            .from('run_segments')
            .select('run_id, trail_segment_id')
            .in('run_id', runIds);

          const trailCountMap = new Map<string, number>();
          if (segmentCounts) {
            segmentCounts.forEach(seg => {
              const count = trailCountMap.get(seg.run_id) || 0;
              trailCountMap.set(seg.run_id, count + 1);
            });
          }

          // Fetch featured vehicles for users
          const userIds = [...new Set(runs.map(r => r.user_id))];
          const { data: featuredVehicles } = await supabase
            .from('user_vehicles')
            .select('user_id, primary_photo_url')
            .in('user_id', userIds)
            .eq('is_featured', true);
          const featuredVehicleMap = new Map(featuredVehicles?.map(v => [v.user_id, v.primary_photo_url]) || []);

          runs.forEach(run => {
            const photos = Array.isArray(run.photo_urls) 
              ? (run.photo_urls as string[]).filter(url => typeof url === 'string')
              : [];
            
            activities.push({
              id: run.id,
              type: 'run',
              timestamp: run.created_at,
              title: run.title || run.ai_generated_title || `${run.equipment_type.replace('_', ' ')} Run`,
              description: `Completed a run`,
              metadata: {
                distance: run.distance_miles,
                duration: run.duration_minutes,
                startTime: run.start_time,
                photos: photos,
                trailCount: trailCountMap.get(run.id) || 0,
              },
              userId: run.user_id,
              user: run.user ? {
                id: run.user.id,
                username: run.user.username || 'Anonymous',
                avatar_url: run.user.avatar_url,
                featured_vehicle_photo_url: featuredVehicleMap.get(run.user_id) || undefined,
              } : undefined,
              vehicle: run.vehicle ? {
                nickname: run.vehicle.nickname,
                year: run.vehicle.year,
                make: run.vehicle.make,
                model: run.vehicle.model,
              } : undefined,
            });
          });
        }
      }

      // Fetch activity events (user_joined, achievement_earned, etc.)
      // For 'all' filter, fetch all recent activity events
      const activityQuery = supabase
        .from('activity_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      
      // Apply user filter only for 'mine' and 'following'
      if (filter !== 'all' && targetUserIds.length > 0) {
        activityQuery.in('user_id', targetUserIds);
      } else if (filter !== 'all') {
        // Skip activity events if not 'all' and no target users
      }

      if (filter === 'all' || targetUserIds.length > 0) {
        const { data: activityEvents, error: activityError } = await activityQuery;

        if (activityError) {
          console.error('Error fetching activity events:', activityError);
        } else if (activityEvents) {
          // Fetch user data separately
          const userIds = [...new Set(activityEvents.map(e => e.user_id))];
          const { data: users } = await supabase
            .from('users')
            .select('id, username, avatar_url')
            .in('id', userIds);

          // Fetch featured vehicles for these users
          const { data: featuredVehicles } = await supabase
            .from('user_vehicles')
            .select('user_id, primary_photo_url')
            .in('user_id', userIds)
            .eq('is_featured', true);

          const userMap = new Map(users?.map(u => [u.id, u]) || []);
          const featuredVehicleMap = new Map(featuredVehicles?.map(v => [v.user_id, v.primary_photo_url]) || []);

          activityEvents.forEach(event => {
            const user = userMap.get(event.user_id);
            activities.push({
              id: event.id,
              type: event.activity_type as ActivityType,
              timestamp: event.created_at,
              title: event.activity_type === 'user_joined' 
                ? 'New User Joined' 
                : event.activity_type === 'achievement_earned'
                ? 'Achievement Earned'
                : 'Activity',
              description: event.activity_type,
              metadata: event.metadata as any,
              userId: event.user_id,
              user: user ? {
                id: user.id,
                username: user.username || 'Anonymous',
                avatar_url: user.avatar_url,
                featured_vehicle_photo_url: featuredVehicleMap.get(event.user_id) || undefined,
              } : undefined,
            });
          });
        }
      }

      // Fetch events
      if (targetUserIds.length > 0) {
        const { data: events, error: eventsError } = await supabase
          .from('events')
          .select('*')
          .in('created_by', targetUserIds)
          .order('created_at', { ascending: false })
          .limit(20);

        if (eventsError) {
          console.error('Error fetching events:', eventsError);
        } else if (events) {
          events.forEach(event => {
            activities.push({
              id: event.id,
              type: 'event',
              timestamp: event.created_at,
              title: event.title,
              description: `Created ${event.event_type.replace('_', ' ')} event`,
              metadata: {
                eventType: event.event_type,
                status: event.status,
              },
              userId: event.created_by,
            });
          });
        }
      }

      // Sort all activities by timestamp
      activities.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      return activities;
    },
    enabled: filter === 'mine' ? !!userId : filter === 'local' ? !!localParams : true,
  });
};

// Real-time subscription for activity updates
export const useActivityFeedRealtime = (
  userId: string | undefined,
  filter: ActivityFilterType,
  refetch: () => void
) => {
  useEffect(() => {
    if (!userId || filter !== 'mine') return;

    const eventsChannel = supabase
      .channel('activity-events-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events',
          filter: `created_by=eq.${userId}`,
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    const runsChannel = supabase
      .channel('activity-runs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'runs',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(eventsChannel);
      supabase.removeChannel(runsChannel);
    };
  }, [userId, filter, refetch]);
};
